import React, { useState, useEffect, useCallback } from 'react';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { getCompanyFinancials, getDraftCompanyFinancials } from '$/store/portfolio/actions';
import { connect } from 'react-redux';
import FinancialStatement from '$/components/financialStatement';
import './index.scss';
import { DocumentsClientService } from '$/services';
import { FINANCIALS_CATEGORY, LENDER_PRESENTATIONS_CATEGORY } from '$/components/company-documents-upload/categories';
import { CompanyDocumentSearchQuery } from '$/core/models';

interface ICompanyFinancialsProps {
  currentCompany?: Bixby.Models.Company;
  financialStatements: Bixby.Models.FinancialStatement[];
  portfolioIsLoading: boolean;
  error?: Bixby.Models.BixbyError;
  dispatchGetCompanyFinancial: (
    company: Bixby.Models.Company,
    statementType: Bixby.Enums.FinancialStatementType
  ) => Promise<void>;
  dispatchGetDraftCompanyFinancial: (
    company: Bixby.Models.Company,
    statementType: Bixby.Enums.FinancialStatementType
  ) => Promise<void>;
}

const CompanyFinancials: React.FC<ICompanyFinancialsProps> = props => {
  const {
    dispatchGetCompanyFinancial,
    dispatchGetDraftCompanyFinancial,
    portfolioIsLoading,
    currentCompany,
    financialStatements,
    error,
  } = props;

  const [statementType, setStatementType] = useState(null as Bixby.Enums.FinancialStatementType | null);
  const [companyDocuments, setCompanyDocuments] = useState<Bixby.Models.CompanyDocument[]>([]);
  const [isInitalIncomeLoad, setIsInitalIncomeLoad] = useState(true as boolean);
  const [isInitalBalanceLoad, setIsInitalBalanceLoad] = useState(true as boolean);
  const [isInitalCashLoad, setIsInitalCashLoad] = useState(true as boolean);

  const loadFinancials = useCallback(
    (currentCompany: Bixby.Models.Company, financialStatementType: Bixby.Enums.FinancialStatementType) => {
      const dispatchGetFinancials = currentCompany.isDraft
        ? dispatchGetDraftCompanyFinancial
        : dispatchGetCompanyFinancial;

      const statement = Bixby.Utils.find(financialStatements, item => {
        return item.financialStatementType === financialStatementType && item.isDraft === currentCompany.isDraft;
      });

      if (statement) {
        if (financialStatementType == Bixby.Enums.FinancialStatementType.IncomeStatement && isInitalIncomeLoad) {
          setIsInitalIncomeLoad(false);
          dispatchGetFinancials(currentCompany, financialStatementType);
        } else if (financialStatementType == Bixby.Enums.FinancialStatementType.CashFlow && isInitalCashLoad) {
          setIsInitalCashLoad(false);
          dispatchGetFinancials(currentCompany, financialStatementType);
        } else if (financialStatementType == Bixby.Enums.FinancialStatementType.BalanceSheet && isInitalBalanceLoad) {
          setIsInitalBalanceLoad(false);
          dispatchGetFinancials(currentCompany, financialStatementType);
        }
      }
    },
    [
      dispatchGetCompanyFinancial,
      dispatchGetDraftCompanyFinancial,
      financialStatements,
      isInitalBalanceLoad,
      isInitalCashLoad,
      isInitalIncomeLoad,
    ]
  );

  const getFinancials: (
    currentCompany?: Bixby.Models.Company,
    financialStatementType?: Bixby.Enums.FinancialStatementType | null
  ) => string[][] = (currentCompany, financialStatementType): string[][] => {
    let result: string[][] = [];
    if (!currentCompany) {
      return result;
    }

    if (currentCompany.doesNotHaveFinancials) {
      return result;
    }

    financialStatementType = financialStatementType || Bixby.Enums.FinancialStatementType.IncomeStatement;

    const statement = Bixby.Utils.find(financialStatements, item => {
      return item.financialStatementType === financialStatementType && item.isDraft === currentCompany.isDraft;
    });

    if (statement) {
      result = Object.assign([], statement.data || []);
    }

    return result;
  };

  const handleFinancialChange = (e: any) => {
    if (!portfolioIsLoading && currentCompany) {
      setStatementType(e.target.value);
    }
  };

  useEffect(() => {
    const searchDocuments = async (query: CompanyDocumentSearchQuery) => {
      if (!currentCompany) {
        return;
      }

      if (currentCompany.doesNotHaveFinancials) {
        return;
      }

      const results = await DocumentsClientService.searchDocuments(currentCompany.id, query);

      if (results && results.length > 0) {
        setCompanyDocuments(results);
      }
    };

    const query: CompanyDocumentSearchQuery = {
      companyDocumentCategory_Ids: [FINANCIALS_CATEGORY, LENDER_PRESENTATIONS_CATEGORY],
    };
    searchDocuments(query);
  }, [currentCompany]);

  useEffect(() => {
    if (!portfolioIsLoading && currentCompany && !currentCompany.doesNotHaveFinancials && !error) {
      const financialStatementType = statementType ?? Bixby.Enums.FinancialStatementType.IncomeStatement;
      loadFinancials(currentCompany, financialStatementType);
    }
  }, [currentCompany, portfolioIsLoading, statementType, loadFinancials, error]);

  return (
    <React.Fragment>
      <section>
        <div className="financial_dropdown">
          Select a Statement
          <select onChange={handleFinancialChange} disabled={portfolioIsLoading}>
            <option value={Bixby.Enums.FinancialStatementType.IncomeStatement}>Income Statement</option>
            <option value={Bixby.Enums.FinancialStatementType.BalanceSheet}>Balance Sheet</option>
            <option value={Bixby.Enums.FinancialStatementType.CashFlow}>Cash Flow</option>
          </select>
          <div className="hidden_arrow" />
        </div>
        <div className="financial_table green_sheet_wrap">
          <FinancialStatement
            currentCompany={currentCompany}
            companyFinancial={getFinancials(currentCompany, statementType) || []}
            companyDocuments={companyDocuments}
            isLoading={portfolioIsLoading}
          />
        </div>
      </section>
    </React.Fragment>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    portfolioIsLoading: state.portfolioState.isLoading,
    error: state.portfolioState.error,
    currentCompany: state.portfolioState.currentCompany,
    financialStatements: state.portfolioState.financialStatements,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetCompanyFinancial: (company: Bixby.Models.Company, statementType: Bixby.Enums.FinancialStatementType) =>
    dispatch(getCompanyFinancials(company, statementType)),
  dispatchGetDraftCompanyFinancial: (
    company: Bixby.Models.Company,
    statementType: Bixby.Enums.FinancialStatementType
  ) => dispatch(getDraftCompanyFinancials(company, statementType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyFinancials);
